import { TycoonGame } from '../../../tycoon-engine.js';
import { PROTOCOL_VERSION, MSG_TYPES } from '../../../shared/protocol.js';
import { createRequestId } from '../../realtime/transport.js';

const STORAGE_KEY = 'ivs-board-tycoon-local-state';

function normalizeLocalDisplayName(profile) {
  return String(profile?.displayName || '')
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, 32);
}

export class TycoonController {
  constructor({ storage = globalThis.localStorage } = {}) {
    this.storage = storage;
    this.pendingSeatClaim = null;
    this.pendingAvatarId = null;
    this.startPending = false;
  }

  loadLocal(defaultPlayers = 2, profile = null) {
    let game;
    try {
      const raw = this.storage?.getItem(STORAGE_KEY);
      game = raw
        ? (TycoonGame.fromSnapshot(JSON.parse(raw)) || new TycoonGame(defaultPlayers))
        : new TycoonGame(defaultPlayers);
    } catch {
      game = new TycoonGame(defaultPlayers);
    }
    return this.applyLocalProfile(game, profile);
  }

  saveLocal(game) {
    if (!game || !this.storage) return;
    this.storage.setItem(STORAGE_KEY, JSON.stringify(game.snapshot()));
  }

  createLocal(playerCount = 2, profile = null) {
    return this.applyLocalProfile(new TycoonGame(playerCount), profile);
  }

  applyLocalProfile(game, profile) {
    const firstPlayer = game?.players?.[0];
    const displayName = normalizeLocalDisplayName(profile);
    if (firstPlayer && displayName) firstPlayer.name = displayName;
    return game;
  }

  restoreSnapshot(snapshot) {
    return TycoonGame.fromSnapshot(snapshot);
  }

  resetLobbyPending() {
    this.pendingSeatClaim = null;
    this.pendingAvatarId = null;
    this.startPending = false;
  }

  beginSeatClaim(seat) {
    if (
      !Number.isInteger(seat) ||
      seat < 0 ||
      seat > 3 ||
      Number.isInteger(this.pendingSeatClaim) ||
      Boolean(this.pendingAvatarId) ||
      this.startPending
    ) return false;

    this.pendingSeatClaim = seat;
    return true;
  }

  settleSeatClaim() {
    this.pendingSeatClaim = null;
  }

  beginAvatarUpdate(avatarId) {
    if (
      typeof avatarId !== 'string' ||
      !avatarId ||
      this.pendingAvatarId ||
      Number.isInteger(this.pendingSeatClaim) ||
      this.startPending
    ) return false;

    this.pendingAvatarId = avatarId;
    return true;
  }

  settleAvatarUpdate() {
    this.pendingAvatarId = null;
  }

  beginStart() {
    if (this.startPending || Number.isInteger(this.pendingSeatClaim) || Boolean(this.pendingAvatarId)) return false;
    this.startPending = true;
    return true;
  }

  settleStart() {
    this.startPending = false;
  }

  reconcileAuthoritativeState(state, userSeat) {
    if (Number.isInteger(this.pendingSeatClaim) && userSeat === this.pendingSeatClaim) {
      this.pendingSeatClaim = null;
    }
    if (
      this.pendingAvatarId &&
      Number.isInteger(userSeat) &&
      state?.seats?.[userSeat]?.avatarId === this.pendingAvatarId
    ) {
      this.pendingAvatarId = null;
    }
    if (state?.status !== 'waiting') {
      this.pendingAvatarId = null;
      this.startPending = false;
    }
  }

  onlinePlayerId(state, userSeat) {
    if (!state || !Number.isInteger(userSeat)) return null;
    return state.seats?.[userSeat]?.playerId || null;
  }

  canControlCurrentPlayer({ game, isOnline, state, role, userSeat, pendingRequestId }) {
    if (!game || game.finished) return false;

    if (!isOnline) {
      return Boolean(game.currentPlayer && !game.currentPlayer.bankrupt);
    }

    return Boolean(
      state?.status === 'active' &&
      role === 'player' &&
      !pendingRequestId &&
      this.onlinePlayerId(state, userSeat) === game.currentPlayer?.id &&
      !game.currentPlayer?.bankrupt
    );
  }

  createOnlineAction({ state, action, propertyIndex = null, deviceId }) {
    if (!state || state.status !== 'active') return null;

    const requestId = createRequestId('tycoon');
    return {
      requestId,
      message: {
        type: MSG_TYPES.TYCOON_ACTION,
        protocolVersion: PROTOCOL_VERSION,
        requestId,
        expectedRevision: state.revision,
        action,
        propertyIndex: Number.isInteger(propertyIndex) ? propertyIndex : null,
        deviceId
      }
    };
  }

  applyLocalAction(game, action, propertyIndex = null) {
    if (!game) return false;

    const actions = {
      ROLL_DICE: () => game.rollDice(),
      BUY_PROPERTY: () => game.buyProperty(),
      SKIP_PURCHASE: () => game.skipPurchase(),
      END_TURN: () => game.endTurn(),
      PAY_DEBT: () => game.payDebt(),
      DECLARE_BANKRUPTCY: () => game.declareBankruptcy(),
      PAY_JAIL: () => game.payJailFee(),
      USE_JAIL_CARD: () => game.useJailCard(),
      BUILD: () => game.buildProperty(propertyIndex),
      SELL_BUILDING: () => game.sellBuilding(propertyIndex),
      MORTGAGE: () => game.mortgageProperty(propertyIndex),
      UNMORTGAGE: () => game.unmortgageProperty(propertyIndex)
    };

    const handler = actions[action];
    if (!handler) return false;
    return Boolean(handler());
  }
}
